import * as Yup from 'yup';
import {LectureFormValues} from './types';
import {TTimetableItem} from '../../../../redux/timetable/timetable.type';
import {clearText} from '../../../../component/textEditor/TextEditorHelpers';

/** Max plain-text length for the lecture/lecturer summary fields. */
export const SUMMARY_MAX = 1000;

/**
 * Yup rule for a rich-text summary field. Counts plain-text length (HTML stripped
 * via clearText) so it matches the character counter shown in the editor.
 */
export const summaryMaxRule = (max: number, message: string) =>
    Yup.string().test('summaryMaxLength', message, value => clearText(value || '').length <= max);

/**
 * The lecture `link` field is stored as a JSON string: {title, link}.
 * Older records may hold a plain URL string, so we fall back gracefully.
 */
export const parseLectureLink = (raw?: string | null): {linkName: string; link: string} => {
    if(!raw) return {linkName: '', link: ''};
    try {
        const parsed = JSON.parse(raw);
        if(parsed && typeof parsed === 'object')
            return {linkName: parsed.title ?? '', link: parsed.link ?? ''};
    } catch {
        // Looks like our {title, link} envelope but won't parse — a corrupted/truncated record.
        // Salvage the fields by hand rather than exposing the raw JSON string to the user.
        if(raw.trimStart().startsWith('{')) {
            return {
                linkName: raw.match(/"title"\s*:\s*"([^"]*)/)?.[1] ?? '',
                link: raw.match(/"link"\s*:\s*"([^"]*)/)?.[1] ?? '',
            };
        }
    }
    return {linkName: '', link: raw};
};

export const serializeLectureLink = (linkName: string, link: string): string | null => {
    const title = (linkName ?? '').trim();
    const url = (link ?? '').trim();
    if(!title && !url) return null;
    return JSON.stringify({title, link: url});
};

export const initialValueLecture = (lecture?: TTimetableItem | null): Omit<LectureFormValues, 'dayIndex'> => ({
    title: lecture?.title ?? '',
    lecturerIds: lecture?.lecturers?.map(l => l.lid) ?? [],
    tids: lecture?.tickets?.map(t => t.tid) ?? [],
    thid: lecture?.thid ?? null,
    tsid: lecture?.tsid ?? null,
    description: lecture?.description ?? '',
    amount: lecture ? lecture.amount ?? null : 0,
    price: lecture?.price ?? 0,
    hasCasualPrice: lecture?.priceCasual != null,
    priceCasual: lecture?.priceCasual ?? 0,
    isRequired: !!lecture?.isRequired,
    ...parseLectureLink(lecture?.link),
    active: lecture? !!lecture?.active : true,
});

export const buildValidationSchema = (
    requiredMessage: string,
    linkInvalidMessage: string,
    linkTooLongMessage: string,
    linkNameTooLongMessage: string,
    integerMessage: string,
    descriptionTooLongMessage: string,
) =>
    Yup.object({
        title: Yup.string().required(requiredMessage),
        description: summaryMaxRule(SUMMARY_MAX, descriptionTooLongMessage),
        tsid: Yup.number().nullable().required(requiredMessage),
        dayIndex: Yup.number().min(-1, requiredMessage).required(requiredMessage),
        // Unlimited capacity is stored as null (the field is disabled then); any entered
        // value must be 1+. `defined()` rejects undefined (a cleared field), while the
        // empty string and an explicit 0 are caught by typeError/min — so all three are blocked.
        amount: Yup.number().nullable().defined(requiredMessage).min(1, requiredMessage).typeError(requiredMessage).integer(integerMessage),
        price: Yup.number().min(0).integer(integerMessage),
        priceCasual: Yup.number().min(0).integer(integerMessage),
        link: Yup.string().url(linkInvalidMessage).max(200, linkTooLongMessage),
        linkName: Yup.string().max(20, linkNameTooLongMessage),
    });
